import { useEffect, useState, FormEvent, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client.js";
import { useAuth } from "../context/AuthContext.js";

interface Session {
  id: string; status: string; format?: string; budgetCapMicroeur?: string;
  influencerId?: string; createdAt: string; name?: string | null;
  previewAssetIds?: string[];
}
interface Influencer { id: string; name: string; }
interface IdentityVersion { id: string; status: string; }

export function SessionsPage() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [idVersions, setIdVersions] = useState<IdentityVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({}); // assetId → downloadUrl
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null); // index into allLightboxUrls
  const [allLightboxUrls, setAllLightboxUrls] = useState<string[]>([]);
  const [form, setForm] = useState({ influencerId: "", identityVersionId: "", format: "portrait_9_16", budgetCapEur: "0.50", notes: "" });
  const [formError, setFormError] = useState("");

  function load() {
    setLoading(true);
    Promise.all([
      api.get<{ sessions: Session[] }>("/sessions?limit=30"),
      api.get<{ influencers: Influencer[] }>("/influencers?limit=50"),
    ]).then(([s, inf]) => {
      const sessionList = s.sessions ?? [];
      setSessions(sessionList);
      // Load signed download URLs for preview thumbnails
      const allAssetIds = sessionList.flatMap(sess => sess.previewAssetIds ?? []);
      for (const assetId of allAssetIds) {
        api.get<{ downloadUrl: string }>(`/assets/${assetId}`)
          .then(r => setPreviewUrls(prev => ({ ...prev, [assetId]: r.downloadUrl })))
          .catch(() => {});
      }
      const infs = inf.influencers ?? [];
      setInfluencers(infs);
      if (infs.length && infs[0]) {
        const firstId = infs[0].id;
        setForm(f => ({ ...f, influencerId: firstId }));
        loadIdVersions(firstId);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }

  function loadIdVersions(influencerId: string) {
    if (!influencerId) return;
    api.get<{ identityVersions: IdentityVersion[] }>(`/influencers/${influencerId}/identity-versions`)
      .then(r => {
        const vs = (r.identityVersions ?? []).filter(v => v.status === "approved");
        setIdVersions(vs);
        setForm(f => ({ ...f, identityVersionId: vs[0]?.id ?? "" }));
      })
      .catch(() => setIdVersions([]));
  }

  useEffect(() => { load(); }, []);

  // Rebuild flat list of all preview URLs for lightbox navigation
  useEffect(() => {
    const urls: string[] = [];
    for (const s of sessions) {
      for (const aid of s.previewAssetIds ?? []) {
        const url = previewUrls[aid];
        if (url) urls.push(url);
      }
    }
    setAllLightboxUrls(urls);
  }, [sessions, previewUrls]);

  // Keyboard navigation for lightbox
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const prevLightbox = useCallback(() => setLightboxIdx(i => i !== null && i > 0 ? i - 1 : i), []);
  const nextLightbox = useCallback(() => setLightboxIdx(i => i !== null && i < allLightboxUrls.length - 1 ? i + 1 : i), [allLightboxUrls.length]);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prevLightbox();
      else if (e.key === "ArrowRight") nextLightbox();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxIdx, closeLightbox, prevLightbox, nextLightbox]);

  function handleInfluencerChange(id: string) {
    setForm(f => ({ ...f, influencerId: id, identityVersionId: "" }));
    loadIdVersions(id);
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setFormError("");
    if (!form.identityVersionId) {
      setFormError("Esta influencer no tiene versiones de identidad aprobadas. Ve a su ficha → pestaña Identidad para crear una.");
      return;
    }
    try {
      const body: Record<string, unknown> = {
        influencerId: form.influencerId,
        identityVersionId: form.identityVersionId,
        format: form.format,
      };
      if (form.budgetCapEur) body.budgetCapMicroeur = String(Math.round(parseFloat(form.budgetCapEur) * 1_000_000));
      await api.post("/sessions", body);
      setCreating(false);
      load();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      setFormError(e.data?.message || "Error al crear sesión");
    }
  }

  const statusColor = (s: string) => ({
    draft: "#6366f1", generating: "#d97706", ready: "#2d6a4f",
    exported: "#1d4ed8", cancelled: "#9ca3af"
  }[s] || "#6b7280");

  if (loading) return <div style={{ padding: 24, color: "#888" }}>Cargando…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Sesiones de foto</h1>
          <p style={{ color: "#666", fontSize: 13, margin: "4px 0 0" }}>{sessions.length} sesiones</p>
        </div>
        {(role === "owner" || role === "editor") && (
          <button onClick={() => setCreating(!creating)} style={btnPrim}>+ Nueva sesión</button>
        )}
      </div>

      {creating && (
        <form onSubmit={handleCreate} style={{ background: "#fff", padding: 24, borderRadius: 10, marginBottom: 24, display: "flex", flexDirection: "column", gap: 12, maxWidth: 500 }}>
          <h3 style={{ margin: 0 }}>Nueva sesión</h3>
          {formError && <div style={{ color: "#dc2626", fontSize: 13 }}>{formError}</div>}
          <label style={lbl}>Influencer
            <select value={form.influencerId} onChange={e => handleInfluencerChange(e.target.value)} required style={inp}>
              {influencers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </label>
          <label style={lbl}>Versión de identidad
            {idVersions.length === 0 ? (
              <div style={{ padding: "8px 12px", background: "#fef9c3", borderRadius: 6, fontSize: 12, color: "#92400e" }}>
                Sin versiones aprobadas. <Link to={`/influencers/${form.influencerId}`} style={{ color: "#2d6a4f" }}>Crear en ficha → Identidad</Link>
              </div>
            ) : (
              <select value={form.identityVersionId} onChange={e => setForm({ ...form, identityVersionId: e.target.value })} required style={inp}>
                {idVersions.map(v => <option key={v.id} value={v.id}>{v.id.slice(0, 8)}… ({v.status})</option>)}
              </select>
            )}
          </label>
          <label style={lbl}>Formato
            <select value={form.format} onChange={e => setForm({ ...form, format: e.target.value })} style={inp}>
              <option value="portrait_9_16">Vertical 9:16 (Reels/TikTok)</option>
              <option value="square_1_1">Cuadrado 1:1 (Feed)</option>
              <option value="landscape_16_9">Horizontal 16:9</option>
            </select>
          </label>
          <label style={lbl}>Presupuesto máximo (€)
            <input type="number" step="0.01" min="0.01" value={form.budgetCapEur} onChange={e => setForm({ ...form, budgetCapEur: e.target.value })} style={inp} placeholder="0.50" />
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" style={btnPrim}>Crear sesión</button>
            <button type="button" onClick={() => setCreating(false)} style={btnSec}>Cancelar</button>
          </div>
        </form>
      )}

      {sessions.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 10, padding: 40, textAlign: "center", color: "#888" }}>
          Sin sesiones. Crea una sesión para generar fotos y contenido.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {sessions.reduce<{ els: React.ReactNode[]; offset: number }>((acc, s) => {
            const previews = (s.previewAssetIds ?? []).map(aid => previewUrls[aid]).filter(Boolean) as string[];
            const sessionOffset = acc.offset;
            const el = (
              <Link key={s.id} to={`/sessions/${s.id}`} style={{ textDecoration: "none" }}>
                <div style={{ background: "#fff", borderRadius: 10, padding: 16 }}
                  onMouseEnter={e => (e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.08)")}
                  onMouseLeave={e => (e.currentTarget.style.boxShadow = "none")}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 14, color: "#1a1a1a" }}>{s.name || formatLabel(s.format)}</div>
                      <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>{new Date(s.createdAt).toLocaleDateString("es")}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      {s.budgetCapMicroeur && (
                        <span style={{ fontSize: 12, color: "#666" }}>€{(Number(s.budgetCapMicroeur) / 1_000_000).toFixed(2)}</span>
                      )}
                      <span style={{ fontSize: 11, padding: "2px 10px", borderRadius: 12, background: statusColor(s.status) + "22", color: statusColor(s.status), fontWeight: 600 }}>
                        {s.status}
                      </span>
                      <span style={{ color: "#ccc" }}>→</span>
                    </div>
                  </div>
                  {previews.length > 0 && (
                    <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                      {previews.map((url, i) => (
                        <div
                          key={i}
                          style={{ position: "relative", flexShrink: 0 }}
                          onMouseEnter={e => { e.currentTarget.querySelectorAll<HTMLElement>(".thumb-action").forEach(b => b.style.opacity = "1"); }}
                          onMouseLeave={e => { e.currentTarget.querySelectorAll<HTMLElement>(".thumb-action").forEach(b => b.style.opacity = "0"); }}
                        >
                          <img src={url} alt={`Toma ${i + 1}`}
                            style={{ height: 90, width: "auto", maxWidth: 70, objectFit: "cover", borderRadius: 6, border: "1px solid #eee", display: "block" }} />
                          {/* View large */}
                          <button
                            className="thumb-action"
                            onClick={e => { e.preventDefault(); e.stopPropagation(); setLightboxIdx(sessionOffset + i); }}
                            title="Ver en grande"
                            style={{ position: "absolute", top: 4, left: 4, background: "rgba(0,0,0,0.6)", border: "none", borderRadius: "50%", width: 24, height: 24, cursor: "zoom-in", color: "#fff", fontSize: 12, lineHeight: "24px", textAlign: "center", padding: 0, opacity: 0, transition: "opacity 0.15s" }}
                          >🔍</button>
                          {/* Set as base */}
                          <button
                            className="thumb-action"
                            onClick={e => { e.preventDefault(); e.stopPropagation(); navigate(`/sessions/${s.id}?base=${i}`); }}
                            title="Usar como toma base"
                            style={{ position: "absolute", bottom: 4, left: 0, right: 0, margin: "0 auto", width: "calc(100% - 4px)", background: "rgba(217,119,6,0.85)", border: "none", borderRadius: 4, cursor: "pointer", color: "#fff", fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center", padding: 0, opacity: 0, transition: "opacity 0.15s" }}
                          >⭐ Base</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            );
            acc.els.push(el);  // eslint-disable-line
            acc.offset += previews.length;
            return acc;
          }, { els: [], offset: 0 }).els}
        </div>
      )}

      {/* Lightbox with prev/next */}
      {lightboxIdx !== null && allLightboxUrls[lightboxIdx] && (
        <div
          onClick={closeLightbox}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out" }}
        >
          <img
            src={allLightboxUrls[lightboxIdx]}
            alt="Vista ampliada"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: "80vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8, boxShadow: "0 8px 40px rgba(0,0,0,0.6)", cursor: "default" }}
          />
          {/* Close */}
          <button onClick={closeLightbox} style={{ position: "fixed", top: 20, right: 24, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 40, height: 40, cursor: "pointer", color: "#fff", fontSize: 20, lineHeight: "40px", textAlign: "center", padding: 0 }}>✕</button>
          {/* Prev */}
          {lightboxIdx > 0 && (
            <button
              onClick={e => { e.stopPropagation(); prevLightbox(); }}
              style={{ position: "fixed", left: 20, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 48, height: 48, cursor: "pointer", color: "#fff", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center" }}
            >‹</button>
          )}
          {/* Next */}
          {lightboxIdx < allLightboxUrls.length - 1 && (
            <button
              onClick={e => { e.stopPropagation(); nextLightbox(); }}
              style={{ position: "fixed", right: 20, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 48, height: 48, cursor: "pointer", color: "#fff", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center" }}
            >›</button>
          )}
          {/* Counter */}
          <div style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.6)", fontSize: 12 }}>
            {lightboxIdx + 1} / {allLightboxUrls.length}
          </div>
        </div>
      )}
    </div>
  );
}

function formatLabel(format?: string): string {
  return { portrait_9_16: "Vertical 9:16 (Reels/TikTok)", square_1_1: "Cuadrado 1:1 (Feed)", landscape_16_9: "Horizontal 16:9" }[format ?? ""] ?? (format || "Sesión");
}

const btnPrim: React.CSSProperties = { padding: "8px 16px", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const btnSec: React.CSSProperties = { padding: "8px 16px", border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", background: "none", fontSize: 13 };
const inp: React.CSSProperties = { padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };
const lbl: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "#444" };
